import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {
  BrowserRouter,
  Route,
  Switch,
} from 'react-router-dom';
import {
  Link,
  Element,
  scroller,
  animateScroll as scroll,
} from 'react-scroll';

import {
  iterUnpack,
  defaultLogoImageUrl,
} from '../services/APIClient';
import SegmentClient from '../services/SegmentClient';
import BrandDetailPhotos from '../components/brand_detail/BrandDetailPhotos';
import BrandDetailProducts from '../components/brand_detail/BrandDetailProducts';
import BrandDetailProjects from '../components/brand_detail/BrandDetailProjects';
import BrandDetailFirms from '../components/brand_detail/BrandDetailFirms';
import BrandDetailArticles from '../components/brand_detail/BrandDetailArticles';
import BrandDetailSimilarBrands from '../components/brand_detail/BrandDetailSimilarBrands';
import BrandDetailAbout from '../components/brand_detail/BrandDetailAbout';
import {
  upgradeMessage,
  handlePremiumLearnMore,
} from '../components/brand_detail/BrandUpgradeTooltip';
import {
  PAGE_NAME,
  PATH_REGEX,
  BASE_URL,
  ANCHOR_OFFSET,
  ANCHOR_DURATION,
  CONTACT_MODAL_HASH,
  BRAND_INQUIRY_GENERAL_BOT_ID,
  BRAND_INQUIRY_PRODUCTS_BOT_ID,
} from '../components/brand_detail/Constants';
import NavBar, {
  NavBarItem,
  StickyContainer,
  NavBarRightSection,
} from '../components/NavBar';
import { UserResource, BrandResource } from '../services/RESTClient';
import RequireRegisterPopup, { PERMISSION_TYPES } from '../components/RequireRegisterPopup';

const root = document.getElementById('root');
const header = document.getElementById('Header');

const BRAND_SLUG = root.getAttribute('data-brand-slug');
const userResource = new UserResource();

const TABS = {
  IMAGES: {
    name: 'Images',
    props: {},
    url: 'photos',
    premiumOnly: true,
    premiumOrder: 5,
    basicOrder: 6,
    component: BrandDetailPhotos,
  },
  PRODUCTS: {
    name: 'Products',
    props: {},
    url: 'products',
    premiumOrder: 3,
    basicOrder: 3,
    component: BrandDetailProducts,
  },
  PROJECTS: {
    name: 'Projects',
    props: {},
    url: 'projects',
    premiumOnly: true,
    premiumOrder: 4,
    basicOrder: 5,
    component: BrandDetailProjects,
  },
  FIRMS: {
    name: 'Firms',
    props: {},
    url: 'firms',
    premiumOnly: true,
    premiumOrder: 2,
    basicOrder: 4,
    component: BrandDetailFirms,
  },
  ABOUT: {
    name: 'About',
    props: {},
    url: 'about',
    premiumOrder: 1,
    basicOrder: 1,
    component: BrandDetailAbout,
  },
  SIMILAR_BRANDS: {
    name: 'Similar Brands',
    props: {},
    url: 'similar-brands',
    basicOnly: true,
    premiumOrder: 7,
    basicOrder: 2,
    isFluid: true,
    component: BrandDetailSimilarBrands,
  },
  ARTICLES: {
    name: 'Articles',
    props: {},
    url: 'articles',
    premiumOnly: true,
    premiumOrder: 6,
    basicOrder: 7,
    component: BrandDetailArticles,
  },
};

function copyTabs() {
  const tabs = [];
  const keys = Object.keys(TABS);
  for (let i = 0; i < keys.length; i += 1) {
    tabs.push(Object.assign({}, TABS[keys[i]]));
  }

  return tabs;
}

let TabsMapping = [];

const StickyNavBar = styled(NavBar).attrs({
  className: 'pb-micro',
})`
  & {
    height: 53px;
    
    .top-bar-right .send-message {
      display: none;
    }
    &.is-sticky {
      .top-bar-right .send-message {
        display: block;
      }
    }
    
    ul.menu {
      align-items: flex-end;
      background: none !important;
    }
    
    &:not(.is-sticky) .tab:nth-child(2) {
      padding-left: 0;
    }
    
    .sticky-logo-brand {
      display: none;
      position: relative;
      width: 60px;
      height: 60px;
      padding: 5px 0;
      box-sizing: content-box;

      .sticky-brand-name {
        position: absolute;
        top: 0;
        left: 100%;
        transform: translate(12px, 5px);
      }
      
      img {
        height: 100%;
        object-fit: contain;
      }
      
    }
    &.is-sticky .sticky-logo-brand {
      display: block;
    }
  }
`;

class BrandDetail extends React.Component {
  constructor(props) {
    super(props);
    const brandDetails = iterUnpack(JSON.parse(window.props));
    const brandDetail = brandDetails.allBrands.length ? brandDetails.allBrands[0] : {};
    this.isPremiumBrand = BrandResource.isPremium(brandDetail);
    this.segment = new SegmentClient();

    this.brandId = brandDetail.djangoId;

    TabsMapping = this.getPreparedTabs();

    this.state = {
      ...this.defaultState(),
      name: brandDetail.name,
      logo: brandDetail.logo.length ? brandDetail.logo[0].mediaItem.thumbnail : '',
    };
  }

  componentDidMount() {
    const { active } = this.props;
    if (active) {
      this.scrollToActiveSection(active);
    }

    userResource.me()
      .then((user) => {
        if (!this.isPremiumBrand && userResource.isBrandAdmin(this.brandId)) {
          TabsMapping = this.getPreparedTabs(false);
          TabsMapping = TabsMapping.map(tab => ({
            ...tab,
            props: {
              ...tab.props,
              emptyMessage: upgradeMessage(() => handlePremiumLearnMore(
                PAGE_NAME,
                this.brandId,
                tab.name,
              )),
              handlePremiumLearnMore: () => handlePremiumLearnMore(
                PAGE_NAME,
                this.brandId,
                tab.name,
              ),
              allowAddition: false,
            },
          }));
        }
        this.setState({ user: user.data });
      })
      .catch(() => {});
  }

  getPreparedTabs(enableFilter = true) {
    let tabs = copyTabs();
    const compareValue = this.isPremiumBrand ? 'premiumOrder' : 'basicOrder';
    tabs = tabs.sort((tab1, tab2) => tab1[compareValue] - tab2[compareValue]);
    if (enableFilter) {
      tabs = tabs.filter(tab => !tab[this.isPremiumBrand ? 'basicOnly' : 'premiumOnly']);
    }

    tabs[tabs.length - 1].props.hideDivider = true;
    // eslint-disable-next-line max-len
    const similarBrandsIndex = tabs.findIndex(tab => tab.component === TABS.SIMILAR_BRANDS.component);
    // here we want to remove the divider  from the section above the similar brands
    if (similarBrandsIndex > 0) {
      tabs[similarBrandsIndex - 1].props.hideDivider = true;
    }

    if (similarBrandsIndex >= 0) {
      // eslint-disable-next-line max-len
      tabs[similarBrandsIndex].props.showUpgradeMessage = !this.isPremiumBrand && userResource.isBrandAdmin(this.brandId);
    }

    return tabs;
  }

  openContactModal = () => document.querySelector(`a[href='${CONTACT_MODAL_HASH}']`).click();

  defaultState = () => ({
    active: '',
    name: '',
    logo: '',
    user: null,
    requireRegisterNext: null,
  });

  pushTabHistory = tab => this.props.history.push(`/${BASE_URL}/${BRAND_SLUG}/${tab.url}/`, {
    active: tab.name,
    previousPage: { title: document.title, url: window.location.href },
  });

  replaceTabHistory = (tab) => {
    const {
      match: {
        params: {
          selectedProductSlug,
          selectedMediaId,
          mediaItemAttributionId,
        },
      },
      location: {
        hash,
      },
    } = this.props;

    if (!selectedProductSlug && !selectedMediaId && !mediaItemAttributionId && !hash) {
      this.props.history.replace(`/${BASE_URL}/${BRAND_SLUG}/${tab.url}/`);
    }
  };

  scrollToActiveSection = (name) => {
    setTimeout(() => {
      if (name === TABS.IMAGES.name) {
        scroll.scrollToTop({ duration: 0 });
      } else {
        scroller.scrollTo(name, { offset: ANCHOR_OFFSET });
      }
    }, 100);
  }

  handleSendMessageClick = (e) => {
    e.preventDefault();
    if (userResource.canContactBrand() && BRAND_SLUG) {
      const contact = () => window.openBrandMessagePopup();

      if (userResource.isGeneric() || userResource.isNonProfessional()) {
        this.segment.identify({ force: true, callback: () => contact() });
      } else {
        contact();
      }
    } else {
      this.setState({
        openRequireRegister: true,
        requireRegisterNext: `/${BASE_URL}/${BRAND_SLUG}/${CONTACT_MODAL_HASH}`,
      });
    }
  }

  renderElement = (props, tab) => (
    <Element name={tab.name} key={tab.name}>
      <tab.component {...props} />
    </Element>
  );

  render() {
    const { name, logo } = this.state;

    return (
      <StickyContainer className="row align-center">
        <StickyNavBar
          sticky
          stickyContainerClass="small-12"
          topOffset={header ? header.offsetHeight : 48}
          bottomOffset={header ? header.offsetHeight : 48}
          itemsContainerClass="xlarge-8 medium-10 small-12 column m-auto"
        >
          <NavBarItem className="p-0">
            <div className="sticky-logo-brand">
              <img alt={name} src={logo || defaultLogoImageUrl} />
              <h4 className="sticky-brand-name">{name}</h4>
            </div>
          </NavBarItem>
          {TabsMapping.map(tab => (
            <NavBarItem key={tab.name} className="tab">
              <Link
                activeClass="gray-900"
                to={tab.name}
                spy
                smooth
                offset={ANCHOR_OFFSET}
                duration={ANCHOR_DURATION}
                onClick={() => this.pushTabHistory(tab)}
                onSetActive={() => this.replaceTabHistory(tab)}
              >
                {tab.name}
              </Link>
            </NavBarItem>
          ))}
          <NavBarRightSection>
            <NavBarItem>
              <button
                onClick={this.handleSendMessageClick}
                className="button send-message white pt-xs pb-xs pl-s pr-s fw-medium"
                disabled={false}
                type="button"
              >
                <>
                  <span className="material-icons fs-s mr-s">chat_bubble</span>
                  {' '}
                  Send Message
                </>
              </button>
            </NavBarItem>
          </NavBarRightSection>
        </StickyNavBar>
        {TabsMapping.map(tab => (
          <div className={tab.isFluid ? 'full-width' : 'columns xlarge-8 medium-10 small-12 mt-base'} key={tab.name}>
            {this.renderElement({
              ...this.props,
              ...tab.props,
            }, tab)}
          </div>
        ))}
        <div style={{ height: 85 }} />
        {this.state.openRequireRegister && (
        <RequireRegisterPopup
          permissionType={PERMISSION_TYPES.CONTACT}
          permissionDestinationUrl={`${window.location.href}${CONTACT_MODAL_HASH}`}
          onClose={() => this.setState({ openRequireRegister: false })}
          nextParam={this.state.requireRegisterNext}
        />
        )}
        {/* Fake triggers for Intercom Brand Contact Bot */}
        <span id={BRAND_INQUIRY_GENERAL_BOT_ID} className="p-a invisible" />
        <span id={BRAND_INQUIRY_PRODUCTS_BOT_ID} className="p-a invisible" />
      </StickyContainer>
    );
  }
}

BrandDetail.propTypes = {
  active: PropTypes.string,
  match: PropTypes.shape({
    url: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    location: PropTypes.shape({
      state: PropTypes.shape({}),
      pathname: PropTypes.string,
    }).isRequired,
    push: PropTypes.func,
    replace: PropTypes.func,
    listen: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};

BrandDetail.defaultProps = {
  active: '',
  name: '',
  logo: '',
};

const renderRoute = (props, tab) => <BrandDetail {...props} active={tab.name} />;

if (root) {
  ReactDOM.render(
    <BrowserRouter>
      <Switch>
        {/* Photos routes */}
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/${TABS.IMAGES.url}/:mediaItemAttributionId(${PATH_REGEX.ID})/`}
          render={props => renderRoute(props, TABS.IMAGES)}
        />
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/${TABS.IMAGES.url}/`}
          render={props => renderRoute(props, TABS.IMAGES)}
        />

        {/* Products tab routes */}
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/${TABS.PRODUCTS.url}/:selectedProductSlug(${PATH_REGEX.SLUG})/:selectedMediaId(${PATH_REGEX.SLUG})?`}
          render={props => renderRoute(props, TABS.PRODUCTS)}
        />
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/${TABS.PRODUCTS.url}/:selectedProductSlug(${PATH_REGEX.SLUG})?`}
          render={props => renderRoute(props, TABS.PRODUCTS)}
        />
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/${TABS.PRODUCTS.url}/`}
          render={props => renderRoute(props, TABS.PRODUCTS)}
        />

        {/* Projects tab routes */}
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/${TABS.PROJECTS.url}/`}
          render={props => renderRoute(props, TABS.PROJECTS)}
        />

        {/* Firms tab routes */}
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/${TABS.FIRMS.url}/`}
          render={props => renderRoute(props, TABS.FIRMS)}
        />

        {/* About tab routes */}
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/${TABS.ABOUT.url}/`}
          render={props => renderRoute(props, TABS.ABOUT)}
        />

        {/* Simillar brands tab routes */}
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/${TABS.SIMILAR_BRANDS.url}/`}
          render={props => renderRoute(props, TABS.SIMILAR_BRANDS)}
        />

        {/* Articles tab routes */}
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/${TABS.ARTICLES.url}/`}
          render={props => renderRoute(props, TABS.ARTICLES)}
        />

        {/* Default route */}
        <Route
          path={`/${BASE_URL}/${BRAND_SLUG}/`}
          render={props => renderRoute(props, TABS.IMAGES)}
        />
      </Switch>
    </BrowserRouter>,
    root,
  );
}
